// Performance-optimized SwipeOutblock component
class ScxSwiperOutblock extends HTMLElement {
  constructor() {
    super();
    
    // Cache and performance optimizations
    this.resizeObserver = null;
    this._isInitialized = false;
    this.cachedWidth = null;
    this.animationFrame = null;
    
    // Bind methods once for better performance
    this.handleResize = this.handleResize.bind(this);
    this.updateBannerSizes = this.updateBannerSizes.bind(this);
  }

  connectedCallback() {
    if (this._isInitialized) return;
    
    this.firstSwiperElem = this.querySelector(this.dataset.swiperElem);
    this.bannerElem = this.querySelectorAll(this.dataset.bannerId);

    if (!this.firstSwiperElem || !this.bannerElem?.length) {
      return;
    }    

    this.initializeObserver();
    this._isInitialized = true;
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initializeObserver() {
    // More efficient ResizeObserver with throttling
    this.resizeObserver = new ResizeObserver(entries => {
      // Cancel any pending animation frame
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }
      
      // Use requestAnimationFrame for smoother performance
      this.animationFrame = requestAnimationFrame(() => {
        for (const entry of entries) {
          this.handleResize(entry);
        }
        this.animationFrame = null;
      });
    });

    this.resizeObserver.observe(this.firstSwiperElem);
  }

  handleResize(entry) {
    const width = entry.contentRect.width;
    
    // Skip if width hasn't changed significantly (performance optimization)
    if (this.cachedWidth && Math.abs(this.cachedWidth - width) < 1) {
      return;
    }
    
    this.cachedWidth = width;
    this.updateBannerSizes(width);
  }

  updateBannerSizes(width) {
    // Use more efficient approach - avoid style recalculation loops
    const widthPx = `${width}px`;
    
    // Batch DOM operations for better performance
    this.bannerElem.forEach(banner => {
      if (!banner.style.width || banner.style.width !== widthPx) {
        banner.style.width = widthPx;
      }
    });
  }

  cleanup() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    this._isInitialized = false;
    this.cachedWidth = null;
  }

  // Public method to force update (useful for dynamic content)
  forceUpdate() {
    if (this.firstSwiperElem) {
      const rect = this.firstSwiperElem.getBoundingClientRect();
      this.updateBannerSizes(rect.width);
    }
  }
}

customElements.define("scx-swiper-outblock", ScxSwiperOutblock);